<template>
  <div class="segment">
    <button
      v-for="(option, index) in visibleButtonOptions"
      :key="index"
      :data-cy="option?.dataCy ?? ''"
      :disabled="option.disabled"
      :class="[
        'segment__button',
        {
          'segment__button--active': isButtonActive(option),
        },
      ]"
      @click="setActiveButton(option, false, index)"
    >
      {{ option.display }}
    </button>

    <div ref="menuRef" class="segment__menu-container">
      <button v-if="showMore" class="segment__button" @click="toggleMenu">
        {{ $t('MORE') }}

        <ChevronIcon class="segment__chevron-icon" direction="down" />
      </button>

      <div v-if="isMenuVisible" class="segment__menu">
        <button
          v-for="(option, index) in menuButtonOptions"
          :key="index"
          :class="[
            'segment__menu-item',
            { 'segment__menu-item--active': isButtonActive(option) },
          ]"
          @click="setActiveButton(option, true, index)"
        >
          {{ option.display }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ChevronIcon from '@/components/icon_components/ChevronIcon.vue'
import { defineComponent, type PropType } from 'vue'

interface ButtonOptions {
  display: string
  value: any
  disabled?: boolean
  dataCy?: string
}

export default defineComponent({
  components: {
    ChevronIcon,
  },
  props: {
    maxButtons: {
      type: Number,
      required: false,
      default: 3,
    },
    options: {
      type: Array as PropType<ButtonOptions[]>,
      required: true,
      deep: true,
    },
    activeButtonIndex: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  emits: ['onSetActiveButton'],

  data() {
    return {
      activeButtonOption: this.options[this.activeButtonIndex],
      isMenuVisible: false,
      optionHistory: this.options.slice(), // Copy into new array
    }
  },

  computed: {
    visibleButtonOptions(): ButtonOptions[] {
      return this.optionHistory.slice(0, this.maxButtons)
    },

    menuButtonOptions(): ButtonOptions[] {
      return this.options
    },

    showMore() {
      return this.options.length > this.maxButtons
    },

    button() {
      return {
        button: this.activeButtonOption.value,
      }
    },
  },
  watch: {
    options: {
      handler(newOptions) {
        // Update optionHistory with new options while preserving order where possible
        this.optionHistory = newOptions.slice()
        // Update activeButtonOption if it exists in the new options
        const currentActiveValue = this.activeButtonOption?.value
        const updatedActiveOption = newOptions.find(
          (option) => option.value === currentActiveValue
        )
        if (updatedActiveOption) {
          this.activeButtonOption = updatedActiveOption
        }
      },
      deep: true,
    },
    activeButtonIndex(newIndex) {
      this.activeButtonOption = this.options[newIndex]
    },
  },

  mounted() {
    window.addEventListener('click', this.onDocumentClick, { capture: true })
  },

  beforeDestroy() {
    window.removeEventListener('click', this.onDocumentClick, { capture: true })
  },

  methods: {
    isButtonActive(button: ButtonOptions) {
      return this.activeButtonOption?.value === button.value
    },

    setActiveButton(
      activeButtonOption: ButtonOptions,
      isMenuButton?: boolean,
      index?: number
    ) {
      this.activeButtonOption = activeButtonOption

      if (!this.visibleButtonOptions.includes(activeButtonOption)) {
        this.optionHistory.unshift(activeButtonOption)
      }
      if (isMenuButton) {
        this.toggleMenu(false)
      }
      this.$emit('onSetActiveButton', {
        index,
        value: activeButtonOption.value,
      })
    },

    toggleMenu(isMenuVisible?: boolean) {
      if (isMenuVisible === undefined) {
        isMenuVisible = !this.isMenuVisible
      }
      this.isMenuVisible = isMenuVisible
    },

    onDocumentClick(e: MouseEvent) {
      const menuElement = this.$refs.menuRef as Element | undefined
      if (!menuElement) return

      const clickedInsideMenu = menuElement.contains(e.target as Node)
      if (!clickedInsideMenu) {
        this.toggleMenu(false)
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.segment {
  display: flex;
  flex-wrap: nowrap;
  padding: 2px;
  border-radius: $border-radius-2xl;
  background: $color-pebble;
  height: 38px;

  &__button {
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: $spacing-5xs;
    padding: $spacing-4xs $spacing-xs;
    border: 1px solid transparent;
    border-radius: $border-radius-xl;
    background: none;
    color: $color-text;
    font-size: $font-size-s;
    font-weight: 700;
    cursor: pointer;

    &:hover {
      color: $color-blue-60;
    }

    &:disabled {
      cursor: not-allowed;
      color: $color-text-light;
    }

    &--active {
      border: 1px solid $color-border;
      background-color: $color-canvas;
      color: $color-navigation;
      box-shadow: 0 0 4px #00000022;
    }
  }

  &__chevron-icon {
    align-self: center;
    width: $spacing-3xs;
    height: $spacing-3xs;
    color: $color-text-light;
  }

  &__menu-container {
    position: relative;
    display: flex;
  }

  &__menu {
    position: absolute;
    top: 100%;
    right: 0;
    display: grid;
    min-width: 100px;
    max-height: 70vh;
    padding: $spacing-4xs 0;
    border: 1px solid $color-border;
    border-radius: $border-radius-s;
    background: $color-canvas;
    box-shadow: 0px 30px 40px -10px #00000022;
    overflow-x: hidden;
    overflow-y: auto;
    z-index: 10;
  }

  &__menu-item {
    padding: $spacing-4xs $spacing-xs;
    border: none;
    text-align: left;
    font-size: $font-size-s;
    background: none;
    color: $color-text;
    cursor: pointer;

    &:hover {
      background-color: $color-pebble;
      color: $color-text;
    }
    &--active {
      font-weight: 700;
    }
  }
}
</style>
